import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Flex } from './shared/Flex';
import { theme } from '../style/theme';
// @ts-ignore
import wcLogo from '../assets/wcLogo.svg'

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${theme.colors.neutral.white};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const LoaderContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${theme.spacing[8]};
`;

const LogoContainer = styled.div`
  width: 319px;
  height: 105px;
  margin-bottom: ${theme.spacing[6]};
  animation: ${pulse} 2s ease-in-out infinite;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const SpinnerContainer = styled.div`
  width: 60px;
  height: 60px;
  margin-bottom: ${theme.spacing[6]};
  position: relative;
`;

const SpinnerRing = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid ${theme.colors.neutral.gray200};
  border-top: 3px solid ${theme.colors.primary.main};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingTitle = styled.h2`
  color: ${theme.colors.neutral.gray900};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-bottom: ${theme.spacing[2]};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const LoadingMessage = styled.p`
  color: ${theme.colors.neutral.gray600};
  font-size: ${theme.typography.fontSize.base};
  margin: 0 0 ${theme.spacing[4]} 0;
  max-width: 400px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ProgressContainer = styled.div`
  width: 100%;
  max-width: 300px;
  margin-top: ${theme.spacing[4]};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${theme.colors.neutral.gray200};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: ${theme.colors.primary.gradient};
  border-radius: ${theme.borderRadius.full};
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral.gray500};
  margin-top: ${theme.spacing[2]};
  text-align: center;
`;

interface AppLoaderProps {
  progress?: number;
  message?: string;
}

const AppLoader: React.FC<AppLoaderProps> = ({ 
  progress = 0, 
  message = "Initializing BioSense Scanner..." 
}) => {
  return (
    <LoaderOverlay>
      <LoaderContainer>
        <LogoContainer>
          <img src={wcLogo} alt="WellthCare Logo" />
        </LogoContainer>
        
        <SpinnerContainer>
          <SpinnerRing />
        </SpinnerContainer>
        
        <LoadingTitle>Loading BioSense Scanner</LoadingTitle>
        <LoadingMessage>{message}</LoadingMessage>
        
        {progress > 0 && (
          <ProgressContainer>
            <ProgressBar>
              <ProgressFill progress={progress} />
            </ProgressBar>
            <ProgressText>{Math.round(progress)}% Complete</ProgressText>
          </ProgressContainer>
        )}
      </LoaderContainer>
    </LoaderOverlay>
  );
};

export default AppLoader;